import * as React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { Box } from '@mui/material';
import NotificationConditionsFilter from './NotificationConditionsFilter';
import { Notification } from '../../Interfaces/Notifications/Notification';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import {
  registerNotification,
  resetNotificationsFilter,
  fetchRegisteredUsers,
  updateNotification,
  saveNotificationsFilter,
  fetchActiveNotifications,
  fetchNotificationNames,
  saveNotificationsReportFilter,
  resetNotificationsReportFilter,
} from '../../store/Notifications/NotificationsActions';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { useEffect } from 'react';
import {
  getDateWithoutTimezone,
  isNullOrEmptyString,
  postJsonData,
} from '../../utils/utils';
import {
  DropDownListChangeEvent,
  MultiSelect,
  MultiSelectChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { NotificationUser } from '../../Interfaces/Notifications/NotificationUser';
import {
  daysOfWeekMap,
  daysTextToTypeMap,
  frequencyTextToTypeMap,
  getDaysOfWeekOptions,
  getNotificationFrequencyOptions,
  notificationFrequencyMap,
} from '../../utils/textMappings';
import { TimePicker } from '@progress/kendo-react-dateinputs';
import StringResources from '../../utils/stringResources';
import CurrentIssueDropDown from './CurrentIssueDropDown';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { ASSET } from '../../utils/constants';
import ReportsFilter from '../Reports/ReportsFilter';
import { fetchCategories } from '../../store/Reports/ReportsActions';
import { ReportCategory } from '../../Interfaces/Reports/ReportCategory';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import { GetString } from '../StringResourceManager';
import { ReportInfo } from '../../Interfaces/Reports/ReportInfo';
import DynamicDropDownList from '../DynamicDropDownList';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridAlign: {
      display: 'flex',
      alignItems: 'center',
    },
    alignGridItems: {
      display: 'flex',
      flexDirection: 'column',
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    radioButtonLabel: {
      fontSize: '1.05rem',
      display: 'inline',
    },
    box: {
      height: 60,
      display: 'flex',
      borderRadius: 5,
      padding: theme.spacing(2),
      margin: theme.spacing(2),
      backgroundColor: '#fff',
    },
    spreadBox: {
      justifyContent: 'space-around',
      alignItems: 'center',
    },
    dialogAction: {
      height: '3rem',
      width: '7em',
      padding: theme.spacing(2),
    },
    actionBox: {
      bottom: 0,
      width: '100%',
    },
    inputBox: {
      width: '300px',
    },
    dropDownAlign: {
      paddingTop: theme.spacing(3.5),
    },
    statusCategoryInput: {
      width: '250px',
      minWidth: '5%',
    },
  })
);

interface Props {
  notificationType: string;
  notificationName: string;
  editView: boolean;
  closeForm: () => void;
  showRegistrationStatus: () => void;
}

const NotificationRuleForm: React.FC<Props> = ({
  notificationType,
  notificationName,
  editView,
  closeForm,
  showRegistrationStatus,
}): React.ReactElement => {
  const classes = useStyles();
  const [users, setUsers] = React.useState<string[]>([]);
  const [notificationRuleName, setNotificationRuleName] = React.useState('');
  const [notificationId, setNotificationId] = React.useState(0);
  const [initialRuleName, setInitialRuleName] = React.useState('');
  const [notificationFrequency, setNotificationFrequency] = React.useState('');
  const [scheduledTime, setScheduledTime] = React.useState<Date>();
  const [dayOfWeek, setDayOfWeek] = React.useState<ApiEnums.DaysOfWeek>();
  const [dayOfMonth, setDayOfMonth] = React.useState<number>();
  const [isValidName, setIsValidName] = React.useState(false);
  const [showWeeklyData, setShowWeeklyData] = React.useState(false);
  const [showMonthlyData, setShowMonthlyData] = React.useState(false);
  const [showTimePicker, setShowTimePicker] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState('');
  const [selectedValue, setSelectedValue] = React.useState('');
  const [reportName, setReportName] = React.useState('');

  const daysOfWeekData: string[] = getDaysOfWeekOptions();
  const daysOfMonthData: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
  const IMMEDIATE = GetString('IMMEDIATE_STR');
  const DAILY = GetString('DAILY_STR');
  const WEEKLY = GetString('WEEKLY_STR');
  const MONTHLY = GetString('MONTHLY_STR');
  const SCHEDULE_TIME = GetString('SCHEDULE_TIME_STR');
  const SELECT_WEEK_DAY = GetString('SELECT_WEEK_DAY_STR');
  const SELECT_MONTH_DAY = GetString('SELECT_MONTH_DAY_STR');
  const MONTH_DAY_TOOLTIP = GetString('MONTH_DAY_TOOLTIP');
  const SELECT_ALL = GetString('SELECT_ALL');
  const SELECT_REPORTS_FILTER = GetString('SELECT_REPORTS_FILTER');

  const usersList: NotificationUser[] = useSelector(
    (state: ApplicationState) => state.notificationsReducer.registeredUsers
  );
  const notificationRuleNames: string[] = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.notificationRuleNames
  );
  const selectedFilter = useSelector(
    (state: ApplicationState) => state.notificationsReducer.notificationsFilter
  );
  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const reportCategories: ReportCategory[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.categories
  );

  const notificationsReportFilter: ReportFilter = useSelector(
    (state: ApplicationState) => state.notificationsReducer.reportFilter
  );

  const usersDropdownList = [
    SELECT_ALL,
    ...usersList.map(user => user.UserName),
  ];

  const dispatch: Dispatch<any> = useDispatch();

  const isNameValid = (name: string) => {
    if (isNullOrEmptyString(name)) return true;
    if (name == initialRuleName) return false;
    return notificationRuleNames.includes(name);
  };

  const onNotificationFrequencyChanged = (e: DropDownListChangeEvent) => {
    setNotificationFrequency(e.target.value);
    switch (e.target.value) {
      case IMMEDIATE:
        setScheduledTime(undefined);
        setShowTimePicker(false);
        setDayOfWeek(undefined);
        setDayOfMonth(undefined);
        setShowMonthlyData(false);
        setShowWeeklyData(false);
        break;
      case DAILY:
        setShowTimePicker(true);
        setDayOfWeek(undefined);
        setDayOfMonth(undefined);
        setShowMonthlyData(false);
        setShowWeeklyData(false);
        break;
      case WEEKLY:
        setShowTimePicker(true);
        setDayOfMonth(undefined);
        setShowMonthlyData(false);
        setShowWeeklyData(true);
        break;
      case MONTHLY:
        setShowTimePicker(true);
        setDayOfWeek(undefined);
        setShowWeeklyData(false);
        setShowMonthlyData(true);
        break;
      default:
        break;
    }
  };

  const handleRuleNameChange = (event: any) => {
    const name = event.target.value;
    setNotificationRuleName(name);
    setIsValidName(!isNameValid(name));
  };

  const allUsersSelected = users.length === usersList.length;
  const handleUserSelect = (event: MultiSelectChangeEvent) => {
    setUsers(event.value);
    const selected = event.value as string[];
    if (selected.includes(SELECT_ALL)) {
      if (allUsersSelected) {
        setUsers([]);
      } else {
        setUsers(usersList.map(user => user.UserName));
      }
    } else {
      setUsers(selected.filter(user => user !== SELECT_ALL));
    }
  };
  const handleScheduledTime = (event: any) => {
    setScheduledTime(event.target.value);
  };
  const handleDayofWeekChange = (event: DropDownListChangeEvent) => {
    setDayOfWeek(event.value);
  };
  const handleDayofMonthChange = (event: DropDownListChangeEvent) => {
    setDayOfMonth(event.value);
  };
  const handleCategoryChange = (category: any) => {
    setSelectedCategory(category);
  };
  const handleValueChange = (value: any) => {
    setSelectedValue(value);
  };

  const convertString = (stringKey: any) => {
    const stringResources = StringResources;
    var string: any = '';
    switch (selectedLanguage) {
      case StringResources.English.ENGLISH_OPTION:
        string = stringResources.English;
        break;
      case StringResources.English.FRENCH_OPTION:
        string = stringResources.French;
        break;
      default:
        string = stringResources.English;
        break;
    }
    return string[stringKey];
  };

  const stringToDate = (time: string | undefined) => {
    if (!time) return undefined;
    const [hours, mins] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, mins);
    return date;
  };
  useEffect((): ReturnType<React.EffectCallback> => {
    fetchRegisteredUsers(dispatch);
    return (): void => {};
  }, []);
  useEffect((): ReturnType<React.EffectCallback> => {
    fetchNotificationNames(dispatch, notificationType);
    fetchCategories(dispatch);
    return (): void => {};
  }, [notificationType]);

  const fetchNotificationRuleData = async (
    notificationType: string,
    notificationName: string,
    token: string | undefined
  ) => {
    const data = await postJsonData(
      `notifications/${notificationType}`,
      token,
      notificationName
    );
    return data as Notification;
  };
  useEffect((): ReturnType<React.EffectCallback> => {
    if (notificationName !== '') {
      fetchNotificationRuleData(notificationType, notificationName, token)
        .then(data => {
          setIsValidName(true);
          setNotificationId(data.NotificationId);
          setInitialRuleName(data.NotificationName);
          setNotificationRuleName(data.NotificationName);
          setUsers(findUsernameById(data.UserIds));
          setNotificationFrequency(
            convertString(
              notificationFrequencyMap.get(data.NotificationFrequency)
            )
          );
          if (
            data.NotificationFrequency !==
            ApiEnums.NotificationFrequency.Immediate
          ) {
            setShowTimePicker(true);
          }
          setScheduledTime(stringToDate(data.ScheduledTime));
          if (data.ScheduledDayOfWeek !== null) {
            setShowWeeklyData(true);
          }
          setDayOfWeek(
            convertString(daysOfWeekMap.get(data.ScheduledDayOfWeek))
          );
          if (data.ScheduledDayOfMonth !== null) {
            setShowMonthlyData(true);
          }
          setDayOfMonth(data.ScheduledDayOfMonth);

          if (notificationType === 'DeviceIssues') {
            const currentIssuesFilter = data.NotificationRules.filters.pop();
            if (
              currentIssuesFilter &&
              'field' in currentIssuesFilter &&
              'value' in currentIssuesFilter
            ) {
              setSelectedCategory(currentIssuesFilter.value.Category);
              setSelectedValue(currentIssuesFilter.value.Value);
            }
            saveNotificationsFilter(dispatch, data.NotificationRules);
          } else if (notificationType === 'Reports') {
            const filters = data.NotificationRules.filters;
            const getFilterValue = (field: string) => {
              const filter = filters.find((f: any) => f.field === field);
              if (filter && 'value' in filter)
                return filter ? filter.value : null;
            };

            const reportFilter: ReportFilter = {
              Division: getFilterValue('Division') || '',
              SiteNumbers: getFilterValue('SiteNumber') || [],
              LaneNumbers: getFilterValue('LaneNumber') || [],
              DateType: '',
              DateRange: getFilterValue('DateRange') || 0,
              StartDateTime: getFilterValue('StartDate') || new Date(),
              EndDateTime: getFilterValue('EndDate') || new Date(),
            };
            setReportName(getFilterValue('ReportID'));
            saveNotificationsReportFilter(dispatch, reportFilter);
          } else {
            saveNotificationsFilter(dispatch, data.NotificationRules);
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
    return (): void => {};
  }, [notificationName]);

  const findUsernameById = (id: number[]) => {
    const usernames: string[] = [];
    for (const userId of id) {
      const user = usersList.find(user => user.UserId === userId);
      if (user) {
        usernames.push(user.UserName);
      }
    }
    return usernames;
  };

  const isSaveDisabled = () => {
    return (
      !isValidName ||
      users?.length === 0 ||
      notificationFrequency === '' ||
      (showTimePicker && !scheduledTime) ||
      (notificationFrequency === WEEKLY && !dayOfWeek) ||
      (notificationFrequency === MONTHLY && !dayOfMonth) ||
      (notificationType === 'Reports' &&
        notificationsReportFilter.SiteNumbers.length === 0) ||
      (notificationType === 'Reports' && reportName === '')
    );
  };

  const getUpdatedNotificationRules = () => {
    let updatedNotificationRules: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [],
    };
    if (notificationType === 'DeviceIssues') {
      const currentIssueFilter: FilterDescriptor = {
        field: ASSET.CURRENT_ISSUE,
        operator: 'contains',
        value: {
          Category: selectedCategory,
          Name: ASSET.CURRENT_ISSUE,
          Value: selectedValue,
        },
      };

      const updatedFilters =
        selectedCategory != ''
          ? [...selectedFilter.filters, currentIssueFilter]
          : selectedFilter.filters;

      return (updatedNotificationRules = {
        ...selectedFilter,
        filters: updatedFilters,
      });
    } else if (notificationType === 'Reports') {
      const updatedReportsFilter: (
        | CompositeFilterDescriptor
        | FilterDescriptor
      )[] = [
        {
          field: ASSET.FIELD_DIVISION_NAME,
          operator: 'contains',
          value: notificationsReportFilter.Division,
        },
        {
          field: ASSET.FIELD_STORE_NAME,
          operator: 'contains',
          value: notificationsReportFilter.SiteNumbers,
        },
        {
          field: ASSET.FIELD_LANE_NAME,
          operator: 'contains',
          value: notificationsReportFilter.LaneNumbers,
        },
        {
          field: ASSET.FIELD_REPORT_NAME,
          operator: 'contains',
          value: reportName,
        },
        {
          field: ASSET.FIELD_DATE_RANGE,
          operator: 'contains',
          value: notificationsReportFilter.DateRange,
        },
        {
          field: ASSET.FIELD_START_DATE,
          operator: 'contains',
          value: getDateWithoutTimezone(
            new Date(notificationsReportFilter.StartDateTime)
          ),
        },
        {
          field: ASSET.FIELD_END_DATE,
          operator: 'contains',
          value: getDateWithoutTimezone(
            new Date(notificationsReportFilter.EndDateTime)
          ),
        },
        {
          field: ASSET.FIELD_LANGUAGE,
          operator: 'eq',
          value: selectedLanguage,
        },
      ];

      return (updatedNotificationRules = {
        ...selectedFilter,
        filters: updatedReportsFilter,
      });
    } else {
      return selectedFilter;
    }
  };

  const handleSave = () => {
    const userIds: number[] = [];
    for (const username of users) {
      const user = usersList.find(u => u.UserName === username);
      if (user) {
        userIds.push(user.UserId);
      }
    }
    let payload: Notification = {
      NotificationId: 0,
      NotificationName: notificationRuleName,
      NotificationType: notificationType,
      NotificationRules: getUpdatedNotificationRules(),
      NotificationActive: true,
      NotificationEnabled: true,
      NotificationFrequency: frequencyTextToTypeMap(notificationFrequency),
      UserIds: userIds,
      ScheduledTime: scheduledTime
        ? scheduledTime.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : undefined,
      ScheduledDayOfWeek: dayOfWeek ? daysTextToTypeMap(dayOfWeek) : dayOfWeek,
      ScheduledDayOfMonth: dayOfMonth,
      CreationDateTime: new Date(),
    };
    if (editView) {
      payload.NotificationId = notificationId;
      payload.NotificationName = notificationRuleName;
      payload.UserIds = userIds;
      payload.NotificationFrequency = frequencyTextToTypeMap(
        notificationFrequency
      );
      payload.NotificationRules = getUpdatedNotificationRules();
      payload.ScheduledTime = scheduledTime
        ? scheduledTime.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : undefined;
      payload.ScheduledDayOfWeek = dayOfWeek
        ? daysTextToTypeMap(dayOfWeek)
        : dayOfWeek;
      payload.ScheduledDayOfMonth = dayOfMonth;

      updateNotification(dispatch, payload).then(() =>
        fetchActiveNotifications(dispatch, notificationType)
      );
    } else {
      registerNotification(dispatch, payload).then(() => {
        fetchActiveNotifications(dispatch, notificationType);
      });
    }
    showRegistrationStatus();
    handleClose();
  };
  const handleClose = () => {
    setInitialRuleName('');
    resetNotificationsFilter(dispatch);
    resetNotificationsReportFilter(dispatch);
    closeForm();
  };

  const [isReportsPanelBarVisible, setIsReportsPanelBarVisible] =
    React.useState<boolean>(false);

  const setReportsPanelBarVisibility = (bool: boolean) => {
    setIsReportsPanelBarVisible(bool);
  };

  const getReportTitle = (title: Map<string, string>) => {
    const titles = new Map(Object.entries(title));
    if (selectedLanguage === StringResources.English.ENGLISH_OPTION) {
      return titles.get('English');
    } else if (selectedLanguage === StringResources.English.FRENCH_OPTION) {
      return titles.get('French');
    }
  };

  const getReportNamesList = () => {
    const reports: ReportInfo[] = reportCategories.flatMap(
      category => category.Reports
    );

    return reports.map(report => ({
      reportID: report.ReportID,
      reportTitle: getReportTitle(report.ReportTitle),
    }));
  };

  const handleReportNameChange = (event: DropDownListChangeEvent) => {
    setReportName(event.value.reportID);
  };

  const CheckboxItem = (li: any, itemProps: any) => {
    const itemChildren = (
      <span>
        <input
          type="checkbox"
          name={itemProps.dataItem}
          checked={
            itemProps.dataItem === SELECT_ALL
              ? allUsersSelected
              : itemProps.selected
          }
          onChange={e => itemProps.onClick(itemProps.index, e)}
        ></input>
        &nbsp;{li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  return (
    <React.Fragment>
      <Container>
        <Grid container spacing={2} className={classes.gridAlign}>
          <Grid item xs={12} md={4} style={{ marginBottom: '20px' }}>
            <Typography variant="body1">
              {GetString('NOTIFICATION_RULE_NAME')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <div className={classes.alignGridItems}>
              <Input
                className={classes.inputBox}
                value={notificationRuleName}
                onChange={handleRuleNameChange}
                valid={isValidName}
                required={true}
                ariaDescribedBy={GetString('RULE_NAME_ERROR_MESSAGE')}
              ></Input>
              <Error
                id={GetString('RULE_NAME_ERROR_MESSAGE')}
                style={{ visibility: isValidName ? 'hidden' : 'visible' }}
              >
                {GetString('RULE_NAME_ERROR_MESSAGE')}
              </Error>
            </div>
          </Grid>
          <Grid item xs={4} style={{ paddingTop: '8px' }}>
            <Typography variant="body1" gutterBottom>
              {GetString('SELECT_USER')}
            </Typography>
          </Grid>
          <Grid item xs={8} style={{ paddingTop: '8px' }}>
            <MultiSelect
              style={{ width: '300px' }}
              fillMode="solid"
              autoClose={false}
              data={usersDropdownList}
              onChange={handleUserSelect}
              value={users}
              itemRender={CheckboxItem}
            ></MultiSelect>
          </Grid>

          <Grid item xs={4} style={{ paddingTop: '8px' }}>
            <Typography variant="body1" gutterBottom>
              {GetString('SELECT_FREQUENCY')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={8}
            className={classes.gridAlign}
            style={{ paddingTop: '8px' }}
          >
            <DynamicDropDownList
              data={getNotificationFrequencyOptions(notificationType)}
              value={notificationFrequency}
              onChange={onNotificationFrequencyChanged}
              width={150}
            />
          </Grid>

          {showTimePicker && (
            <Grid
              item
              md={4}
              xs={4}
              className={classes.gridAlign}
              style={{ paddingTop: '0px' }}
            >
              <TimePicker
                label={SCHEDULE_TIME}
                value={scheduledTime}
                format={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                }}
                validityStyles={false}
                formatPlaceholder="formatPattern"
                onChange={handleScheduledTime}
              ></TimePicker>
            </Grid>
          )}
          {showWeeklyData && (
            <Grid
              item
              xs={8}
              className={classes.gridAlign}
              style={{ paddingTop: '0px' }}
            >
              <DynamicDropDownList
                label={SELECT_WEEK_DAY}
                data={daysOfWeekData}
                value={dayOfWeek}
                onChange={handleDayofWeekChange}
              />
            </Grid>
          )}

          {showMonthlyData && (
            <Grid
              item
              xs={8}
              className={classes.gridAlign}
              style={{ paddingTop: '0px' }}
            >
              <div className={classes.dropDownAlign}>
                <DynamicDropDownList
                  label={SELECT_MONTH_DAY}
                  data={daysOfMonthData}
                  value={dayOfMonth}
                  onChange={handleDayofMonthChange}
                />
                <Typography
                  variant="body2"
                  fontSize="0.675rem"
                  fontStyle="italic"
                >
                  {MONTH_DAY_TOOLTIP}
                </Typography>
              </div>
            </Grid>
          )}

          {notificationType === 'DeviceIssues' && (
            <Grid item xs={12} md={12} className={classes.gridAlign}>
              <CurrentIssueDropDown
                onCategoryChange={handleCategoryChange}
                onValueChange={handleValueChange}
                selectedCategory={selectedCategory}
                selectedValue={selectedValue}
              ></CurrentIssueDropDown>
            </Grid>
          )}

          {notificationType === 'Reports' ? (
            <>
              <Grid item md={12} xs={12} className={classes.gridAlign}>
                <Typography variant="body1" gutterBottom>
                  {SELECT_REPORTS_FILTER}
                </Typography>
              </Grid>
              <Grid
                item
                md={12}
                xs={12}
                className={classes.gridAlign}
                style={{ paddingTop: '0px' }}
              >
                <ReportsFilter
                  setIsReportsPanelBarVisible={setReportsPanelBarVisibility}
                  isReportsPage={false}
                />
              </Grid>
              <Grid item xs={4} style={{ paddingTop: '8px' }}>
                <Typography variant="body1" gutterBottom>
                  {GetString('SELECT_REPORT_NAME')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={8}
                className={classes.gridAlign}
                style={{ paddingTop: '8px' }}
              >
                <DynamicDropDownList
                  data={getReportNamesList()}
                  onChange={handleReportNameChange}
                  value={getReportNamesList().find(
                    report => report.reportID === reportName
                  )}
                  textField="reportTitle"
                  dataItemKey="reportID"
                  width={250}
                />
              </Grid>
            </>
          ) : (
            <Grid item md={12} xs={12} className={classes.gridAlign}>
              <NotificationConditionsFilter
                notificationType={notificationType}
              ></NotificationConditionsFilter>
            </Grid>
          )}
        </Grid>
      </Container>

      <Box className={clsx(classes.spreadBox, classes.box)}>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={isSaveDisabled()}
          className={classes.dialogAction}
          onClick={handleSave}
        >
          {GetString('SAVE_BUTTON')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          className={classes.dialogAction}
          onClick={handleClose}
        >
          {GetString('CANCEL_BUTTON')}
        </Button>
      </Box>
    </React.Fragment>
  );
};

export default NotificationRuleForm;
